import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import './App.css';
import { useAuth0 } from '@auth0/auth0-react';
import AnalyticPage from './components/pages/AnalyticPage';
import AnalyticsBreakdownPage from './components/pages/AnalyticsBreakdownPage';
import ActionLogsPage from './components/pages/ActionLogsPage';
import MainPage from './components/pages/MainPage';
import DevicesPage from './components/pages/DevicesPage';
import CoreDhcpDevicesPage from './components/pages/CoreDhcpDevicesPage';
import CoreDevicesPage from './components/pages/CoreDevicesPage';
import DistroDhcpDevicesPage from './components/pages/DistroDhcpDevicesPage';
import DistroDevicesPage from './components/pages/DistroDevicesPage';
import IspDevicesPage from './components/pages/IspDevicesPage';
import AccessSwitchesPage from './components/pages/AccessSwitchesPage';
import UserManagementPage from './components/pages/UserManagementPage';
import NotFoundPage from './components/pages/NotFoundPage';
import { CircularProgress } from '@mui/material';
import { ApiInstance } from './api/api';
import { utils_instance } from './utils/Utils';
import { UserPublicDataType } from './types/authTypes';
import { CrumbConfig } from './types/breadcrumbTypes';
import { Entities } from './types/types';
import { UserContext } from './context/userConext';
import ProtectedRoute from './routes/ProtectedRoute';
import UnauthorizedPage from './components/pages/UnauthorizedPage';
import ManagementLayout from './components/layouts/ManagementLayout';
import BrokenManagementLinkPage from './components/pages/BrokenManagementLinkPage';
import { actionLogUsersLoader } from './loaders/actionLogUsersLoader';

const _App: React.FC = () => {
  const {
    isAuthenticated,
    user,
    loginWithRedirect,
    isLoading,
    error,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  const [userInfo, setUserInfo] = React.useState<UserPublicDataType | undefined>();
  const [userStatusProgress, setUserStatusProgress] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

  let token = localStorage.getItem('token');

  const getUserInfo = async () => {
    try {
      const user = await ApiInstance.getUserInfo();
      setUserInfo(user);
      setUserStatusProgress(false);
    } catch (e: any) {
      if (e.response?.data?.detail) {
        setErrorMessage(e.response?.data?.detail);
      } else {
        setErrorMessage('Unknown error');
      }
    }
  };

  const loadData = async () => {
    if (isAuthenticated) {
      const token = await getAccessTokenSilently();

      localStorage.setItem('token', token);
      ApiInstance.createAxiosInstance(token);

      getUserInfo();
    }

    if (!isAuthenticated && !isLoading) {
      loginWithRedirect();
    }

    if (token) {
      utils_instance.checkIsValidToken(token, loginWithRedirect);
    }
  };

  useEffect(() => {
    loadData();
  }, [isAuthenticated, isLoading, token]);

  if ((!token && userStatusProgress) || !userInfo) {
    return errorMessage ? <UnauthorizedPage errorMessage={errorMessage} /> : <CircularProgress />;
  }

  const crumb = (config: CrumbConfig) => ({ crumb: config });

  const router = createBrowserRouter([
    {
      path: '/',
      handle: crumb({ icon: 'home', label: 'Home', permitSearchParams: false }),
      children: [
        {
          index: true,
          element: (
            <ProtectedRoute
              user={userInfo}
              permissions={['read_buildings_data_permission']}
              entity={Entities.buildings}
              Component={MainPage}
            />
          ),
        },
        {
          path: 'devices/:building',
          handle: crumb({ resolver: 'building', permitSearchParams: ['region'] }),
          children: [
            {
              index: true,
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={['read_building_devices_data_permission']}
                  entity={Entities.building_manager}
                  Component={DevicesPage}
                />
              ),
            },
            {
              path: 'core/:core',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={['read_core_devices_data_permission']}
                  entity={Entities.core_manager}
                  Component={CoreDevicesPage}
                />
              ),
              handle: crumb({ label: 'Core' }),
            },
            {
              path: 'core-dhcp/:core',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={['read_core_devices_data_permission']}
                  entity={Entities.core_manager}
                  Component={CoreDhcpDevicesPage}
                />
              ),
              handle: crumb({ label: 'Core' }),
            },
            {
              path: 'distro/:distro',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={[
                    'read_distro_devices_arp_data_permission',
                    'read_stack_devices_data_permission',
                  ]}
                  entity={Entities.distro_manager}
                  Component={DistroDevicesPage}
                />
              ),
              handle: crumb({ label: 'Distro' }),
            },
            {
              path: 'distro-dhcp/:distro',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={[
                    'read_distro_devices_arp_data_permission',
                    'read_stack_devices_data_permission',
                  ]}
                  entity={Entities.distro_dhcp_manager}
                  Component={DistroDhcpDevicesPage}
                />
              ),
              handle: crumb({ label: 'Distro' }),
            },
            {
              path: 'isp/:isp',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={[
                    'read_stack_devices_data_permission',
                    'read_switch_devices_data_permission',
                  ]}
                  entity={Entities.isp_manager}
                  Component={IspDevicesPage}
                />
              ),
              handle: crumb({ label: 'ISP' }),
            },
            {
              path: 'switch/:access_switch',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={[
                    'read_stack_devices_data_permission',
                    'read_switch_devices_data_permission',
                  ]}
                  entity={Entities.access_manager}
                  Component={AccessSwitchesPage}
                />
              ),
              handle: crumb({ label: 'Access switch' }),
            },
          ],
        },
        {
          path: 'management',
          element: <ManagementLayout />,
          handle: crumb({ label: 'Management', redirectTo: '/', permitSearchParams: false }),
          children: [
            {
              index: true,
              element: <NotFoundPage />,
            },
            {
              path: 'users',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={['user_management_permission']}
                  entity={Entities.user_management}
                  Component={UserManagementPage}
                />
              ),
              handle: crumb({ icon: 'users', label: 'Users' }),
            },
            {
              path: 'analytics',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={['analytics_permission']}
                  entity={Entities.action_analytics}
                  Component={AnalyticPage}
                />
              ),
              errorElement: <BrokenManagementLinkPage />,
              loader: actionLogUsersLoader,
              shouldRevalidate: () => false,
              handle: crumb({ icon: 'analytics', label: 'Analytics' }),
            },
            {
              path: 'analytics-breakdown',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={['analytics_permission']}
                  entity={Entities.action_analytics_breakdown}
                  Component={AnalyticsBreakdownPage}
                />
              ),
              errorElement: <BrokenManagementLinkPage />,
              loader: actionLogUsersLoader,
              shouldRevalidate: () => false,
              handle: crumb({ icon: 'analytics', label: 'Analytics by Role & User' }),
            },
            {
              path: 'action-logs',
              element: (
                <ProtectedRoute
                  user={userInfo}
                  permissions={['view_action_logs_permission']}
                  entity={Entities.action_logs}
                  Component={ActionLogsPage}
                />
              ),
              handle: crumb({ icon: 'logs', label: 'Action Logs' }),
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ]);

  return (
    <UserContext.Provider value={userInfo}>
      <RouterProvider router={router} />
    </UserContext.Provider>
  );
};

function App() {
  return <_App />;
}

export default App;
